import * as React from 'react';
import { cn } from '@noor/lib/cn';

const inputBaseClass =
  // Figma field: 40px tall, 1px #e5e7eb hairline, field shadow, 14px regular
  // black text, 14px regular #9ca3af placeholder, -0.28px tracking throughout.
  // Dimensions resolve through the shared `--field-*` sizing handles
  // (component-sizing.css) so Input, Select and InputGroup stay
  // height/radius-identical.
  'flex h-[var(--field-height)] w-full min-w-0 rounded-[var(--field-radius)] border border-input-border bg-transparent px-[var(--field-padding-x)] py-2 text-[length:var(--field-font-size)] font-[var(--field-font-weight)] tracking-[var(--field-tracking)] text-input-fg shadow-field transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-[length:var(--field-font-size)] file:font-medium file:text-input-fg placeholder:text-input-placeholder placeholder:text-[length:var(--field-font-size)] placeholder:font-[var(--field-font-weight)] placeholder:tracking-[var(--field-tracking)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-form-control-border-disabled disabled:bg-form-control-bg-disabled disabled:text-form-control-fg-disabled disabled:placeholder:text-form-control-fg-disabled focus-visible:border-input-border-focus focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-input-border-invalid aria-invalid:ring-input-border-invalid/20';

interface InputProps extends React.ComponentProps<'input'> {
  /** Optional icon rendered inside the field, before the text (e.g. a lucide icon). */
  startIcon?: React.ReactNode;
  /** Optional trailing icon/adornment inside the field. */
  endIcon?: React.ReactNode;
}

function Input({ className, type, startIcon, endIcon, onWheel, ...props }: InputProps) {
  /**
   * `type="number"` natively increments/decrements on wheel while focused, so
   * scrolling the page over a focused field silently edits it — a real source
   * of wrong prices and quantities. Blurring on wheel drops the field's claim
   * on the event instead of preventing it, so the page keeps scrolling
   * normally; `preventDefault` here would trap the scroll inside the input.
   */
  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    onWheel?.(event);
    if (type === 'number' && event.currentTarget === document.activeElement) {
      event.currentTarget.blur();
    }
  };

  // Bare input — the common case, keeps a single DOM node.
  if (startIcon === undefined && endIcon === undefined) {
    return (
      <input
        type={type}
        data-slot="input"
        className={cn(inputBaseClass, className)}
        onWheel={handleWheel}
        {...props}
      />
    );
  }

  // Adorned input: a filled field wrapper hosts the icon(s); the inner <input>
  // sheds its own fill/ring so the wrapper owns the surface and focus ring.
  return (
    <div
      data-slot="input-wrapper"
      className={cn(
        'flex h-[var(--field-height)] w-full min-w-0 items-center gap-2 rounded-[var(--field-radius)] border border-input-border bg-transparent px-[var(--field-padding-x)] text-[length:var(--field-font-size)] font-[var(--field-font-weight)] tracking-[var(--field-tracking)] shadow-field transition-[color,box-shadow] focus-within:border-input-border-focus focus-within:ring-[3px] focus-within:ring-ring/50 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:border-form-control-border-disabled has-[input:disabled]:bg-form-control-bg-disabled',
        className,
      )}
    >
      {startIcon !== undefined && (
        <span
          className="flex shrink-0 items-center text-muted-foreground [&_svg]:size-5"
          aria-hidden="true"
        >
          {startIcon}
        </span>
      )}
      <input
        type={type}
        data-slot="input"
        className="h-full min-w-0 flex-1 bg-transparent text-[length:var(--field-font-size)] font-[var(--field-font-weight)] tracking-[var(--field-tracking)] text-input-fg outline-none placeholder:text-[length:var(--field-font-size)] placeholder:font-[var(--field-font-weight)] placeholder:tracking-[var(--field-tracking)] placeholder:text-input-placeholder disabled:pointer-events-none disabled:text-form-control-fg-disabled disabled:placeholder:text-form-control-fg-disabled"
        onWheel={handleWheel}
        {...props}
      />
      {endIcon !== undefined && (
        <span className="flex shrink-0 items-center text-muted-foreground [&_svg]:size-5">
          {endIcon}
        </span>
      )}
    </div>
  );
}

export { Input, type InputProps };
